var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import QRCode from 'qrcode';
import { formatVanityName } from '../../../../common/tour-link';
// The white margin every scanner expects, in modules.
const QUIET_ZONE = 4;
const PNG_SIZE = 2000;
// The logo's white square, as a share of the code's width. Error correction H recovers far more than
// this covers.
const BADGE_SHARE = 0.2;
// Room between the badge's edge and the logo, as a share of the badge.
const BADGE_PADDING = 0.1;
// The longer side a logo is prepared at: well past what the 2000px PNG's badge shows.
const LOGO_SIZE = 512;
export const qrMatrix = (text) => {
    const { modules } = QRCode.create(text, { errorCorrectionLevel: 'H' });
    return { size: modules.size, isDark: (row, col) => Boolean(modules.get(row, col)) };
};
// The badge's square, centred, in the same units as `total`.
const badgeIn = (total) => {
    const size = total * BADGE_SHARE;
    return { x: (total - size) / 2, y: (total - size) / 2, size };
};
const insideOf = (badge) => {
    const pad = badge.size * BADGE_PADDING;
    return { x: badge.x + pad, y: badge.y + pad, size: badge.size - pad * 2 };
};
export const qrSvg = (text, opts) => {
    const matrix = qrMatrix(text);
    const total = matrix.size + QUIET_ZONE * 2;
    const dots = [];
    for (let row = 0; row < matrix.size; row++) {
        for (let col = 0; col < matrix.size; col++) {
            if (matrix.isDark(row, col))
                dots.push(`M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`);
        }
    }
    const badge = badgeIn(total);
    const inside = insideOf(badge);
    const logo = opts.logo === null ? '' : [
        `<rect x="${badge.x}" y="${badge.y}" width="${badge.size}" height="${badge.size}" fill="#ffffff"/>`,
        `<image x="${inside.x}" y="${inside.y}" width="${inside.size}" height="${inside.size}" preserveAspectRatio="xMidYMid meet"`,
        ` xlink:href="${opts.logo}"/>`,
    ].join('');
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${total} ${total}"`,
        ` width="1024" height="1024" shape-rendering="crispEdges">`,
        `<rect width="${total}" height="${total}" fill="#ffffff"/>`,
        `<path d="${dots.join('')}" fill="${opts.dark}"/>`,
        logo,
        '</svg>',
    ].join('');
};
const loadImage = (src) => __awaiter(void 0, void 0, void 0, function* () {
    const image = new Image();
    image.src = src;
    yield image.decode();
    return image;
});
// The logo as both files carry it: drawn once onto a canvas, so each holds a PNG whatever was uploaded
// (print software may not read WebP), and greyed pixel by pixel for one-ink printing rather than by a
// filter that some browsers and design tools ignore. An image that reports no size of its own, such as
// an SVG without one, is drawn square. Should the browser refuse to read the drawing back, the logo
// goes in as it came.
export const prepareLogo = (src, greyscale) => __awaiter(void 0, void 0, void 0, function* () {
    const image = yield loadImage(src);
    const [width, height] = image.naturalWidth > 0 && image.naturalHeight > 0
        ? [image.naturalWidth, image.naturalHeight]
        : [LOGO_SIZE, LOGO_SIZE];
    const fit = LOGO_SIZE / Math.max(width, height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * fit));
    canvas.height = Math.max(1, Math.round(height * fit));
    const context = canvas.getContext('2d');
    if (context === null)
        return src;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    try {
        if (greyscale) {
            const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
            const data = pixels.data;
            for (let i = 0; i < data.length; i += 4) {
                const grey = Math.round(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
                data[i] = grey;
                data[i + 1] = grey;
                data[i + 2] = grey;
            }
            context.putImageData(pixels, 0, 0);
        }
        return canvas.toDataURL('image/png');
    }
    catch (_a) {
        return src;
    }
});
export const qrPng = (text, opts) => __awaiter(void 0, void 0, void 0, function* () {
    const matrix = qrMatrix(text);
    const total = matrix.size + QUIET_ZONE * 2;
    const scale = PNG_SIZE / total;
    const canvas = document.createElement('canvas');
    canvas.width = PNG_SIZE;
    canvas.height = PNG_SIZE;
    const context = canvas.getContext('2d');
    if (context === null)
        throw new Error('This browser cannot draw the QR code.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, PNG_SIZE, PNG_SIZE);
    context.fillStyle = opts.dark;
    for (let row = 0; row < matrix.size; row++) {
        for (let col = 0; col < matrix.size; col++) {
            if (!matrix.isDark(row, col))
                continue;
            // Whole pixels, overlapping a little, so no hairline shows between neighbouring dots.
            const x = Math.floor((col + QUIET_ZONE) * scale);
            const y = Math.floor((row + QUIET_ZONE) * scale);
            context.fillRect(x, y, Math.ceil(scale) + 1, Math.ceil(scale) + 1);
        }
    }
    if (opts.logo !== null) {
        const badge = badgeIn(PNG_SIZE);
        context.fillStyle = '#ffffff';
        context.fillRect(badge.x, badge.y, badge.size, badge.size);
        const image = yield loadImage(opts.logo);
        const inside = insideOf(badge);
        const [naturalWidth, naturalHeight] = image.naturalWidth > 0 && image.naturalHeight > 0
            ? [image.naturalWidth, image.naturalHeight]
            : [inside.size, inside.size];
        const fit = Math.min(inside.size / naturalWidth, inside.size / naturalHeight);
        const [width, height] = [naturalWidth * fit, naturalHeight * fit];
        context.drawImage(image, inside.x + (inside.size - width) / 2, inside.y + (inside.size - height) / 2, width, height);
    }
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob === null ? reject(new Error('The QR code could not be saved.')) : resolve(blob), 'image/png'));
});
// candlelight-tour-qr-hha-color.png: the tour, what it is, whose mark is on it, and how it prints.
export const qrFileName = (base, branding, orgName, color, ext) => {
    const brand = branding === 'org' ? formatVanityName(orgName) || 'org' : branding === 'unboxed' ? '76unboxed' : 'plain';
    return `${base}-qr-${brand}-${color}.${ext}`;
};
